import * as THREE from 'three';
import { hexLen, hexToWorld, type MapDef } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';

/**
 * One alone (a bet, docs/SAGAS.md): Brann's stakes across the mouth of the one road the fight comes down, two staggered
 * rows of sharpened poles leaning out at the nightmares, lashed to a rail, with the old garrison's torn banner on a pole
 * beside them. The other two roads are shut with what was at hand: a cart on its side, logs and stones.
 *
 * Static, built once: everything is one merged mesh (one draw call). No lights.
 */

const WOOD = ['#3d3029', '#4a3a30', '#33281f', '#55443a'];
const TIP = '#6e5e52';
const PLANK = ['#5f4a3c', '#4e3d32', '#6b5446'];
const IRON = '#3a3838';
const STONE = ['#5c5d63', '#4a4b50', '#6a6b70'];
const CLOTH = '#7a2420';
const CLOTH_DARK = '#4e1714';
const BADGE = '#c9ccd2';

/** Where a road meets the clearing (world), and which way it runs out (unit, on the ground). */
const mouthOf = (ctx: OmenCtx, map: MapDef, road: number): { at: THREE.Vector3; yaw: number } | null => {
  const sec = map.sectors[road];
  if (!sec) return null;
  const g = hexToWorld(sec.gate.q, sec.gate.r);
  const at = ctx.at(g.x, g.y, 0);
  // The road's first cell past the gate (the path runs from the far end in, so it is the last one next to the gate).
  const out = [...sec.path].reverse().find((h) => hexLen(h.q - sec.gate.q, h.r - sec.gate.r) === 1 && hexLen(h.q, h.r) > hexLen(sec.gate.q, sec.gate.r));
  const o = out ? hexToWorld(out.q, out.r) : { x: g.x * 2, y: g.y * 2 };
  const to = ctx.at(o.x, o.y, 0);
  return { at, yaw: Math.atan2(to.x - at.x, to.z - at.z) };
};

/** A torn pennant: a cloth with a notched, ragged foot (local: hangs down from y = 0, out along +x). */
const pennant = (): THREE.BufferGeometry => {
  const s = new THREE.Shape();
  s.moveTo(0, 0);
  s.lineTo(0.34, 0);
  s.lineTo(0.33, -0.36);
  s.lineTo(0.27, -0.3);
  s.lineTo(0.22, -0.46);
  s.lineTo(0.15, -0.34);
  s.lineTo(0.08, -0.42);
  s.lineTo(0.02, -0.33);
  s.lineTo(0, -0.4);
  s.closePath();
  return new THREE.ExtrudeGeometry(s, { depth: 0.012, bevelEnabled: false });
};

export const buildStakes = (ctx: OmenCtx, map: MapDef, road: number): THREE.Mesh | null => {
  const kit = new Kit();
  const pole = new THREE.CylinderGeometry(0.032, 0.04, 1, 6);
  const tip = new THREE.ConeGeometry(0.032, 0.12, 6);
  const box = new THREE.BoxGeometry(1, 1, 1);
  const rock = new THREE.DodecahedronGeometry(0.1, 0);
  const wheel = new THREE.CylinderGeometry(0.17, 0.17, 0.05, 10);
  const log = new THREE.CylinderGeometry(0.075, 0.08, 1, 7);
  const rim = new THREE.TorusGeometry(0.17, 0.02, 4, 12);
  const cylAx = new THREE.CylinderGeometry(0.03, 0.03, 1, 6);
  const wood = (i: number): string => WOOD[((i % WOOD.length) + WOOD.length) % WOOD.length] as string;

  const open = mouthOf(ctx, map, road);
  if (open) {
    const { at, yaw } = open;
    const m = place(at.x, ctx.ground(at.x, at.z), at.z, yaw);
    // Two staggered rows across the road, leaning out (local +z is out along the road).
    for (let row = 0; row < 2; row++) {
      const z = row ? -0.24 : -0.08;
      const lean = row ? 0.42 : 0.62;
      for (let i = 0; i < 7; i++) {
        const x = -0.72 + i * 0.24 + (row ? 0.12 : 0);
        if (x > 0.8) continue;
        const len = 0.5 + ((i * 7 + row * 3) % 5) * 0.035;
        const jig = ((i * 13 + row * 5) % 7) / 7 - 0.5;
        // The pole stands from the ground, leaning out.
        const pm = m.clone().multiply(place(x, 0, z, jig * 0.2, 1, lean, jig * 0.12));
        kit.add(pole, wood(i + row), pm, place(0, len / 2, 0, 0, new THREE.Vector3(1, len, 1)));
        kit.add(tip, TIP, pm, place(0, len + 0.05, 0));
      }
    }
    // The rail they're lashed to, low across both rows, and its lashings.
    kit.add(log, wood(2), m, place(0, 0.14, -0.13, 0, new THREE.Vector3(0.6, 1.62, 0.6), 0, Math.PI / 2));
    for (const x of [-0.6, -0.2, 0.2, 0.6]) kit.add(box, '#2a211b', m, place(x, 0.14, -0.13, 0, new THREE.Vector3(0.05, 0.12, 0.13)));
    // Stones packed at their feet.
    for (let i = 0; i < 6; i++) kit.add(rock, STONE[i % 3] as string, m, place(-0.75 + i * 0.3, 0.03, -0.3 + (i % 2) * 0.12, i * 1.3, 0.6 + (i % 3) * 0.2));
    // Brann's banner: a pole off the road's edge, a crossbar, the garrison's red, torn, with its pale tower badge.
    const bx = 0.98;
    const bz = -0.34;
    kit.add(pole, wood(1), m, place(bx, 0.6, bz, 0, new THREE.Vector3(1.1, 1.2, 1.1)));
    kit.add(tip, IRON, m, place(bx, 1.25, bz));
    kit.add(box, wood(3), m, place(bx, 1.12, bz, 0, new THREE.Vector3(0.4, 0.035, 0.035)));
    const cloth = pennant();
    // Hung from the crossbar, facing out down the road, a little stirred.
    const cm = place(bx - 0.18, 1.1, bz + 0.02, 0, 1, 0.12, 0.04);
    kit.add(cloth, CLOTH, m, cm);
    cloth.dispose();
    // A darker hem along its top, and the badge: a little tower in pale stone.
    kit.add(box, CLOTH_DARK, m, place(bx - 0.01, 1.085, bz + 0.03, 0, new THREE.Vector3(0.36, 0.03, 0.02)));
    kit.add(box, BADGE, m, place(bx - 0.01, 0.94, bz + 0.04, 0, new THREE.Vector3(0.07, 0.11, 0.01)));
    kit.add(box, BADGE, m, place(bx - 0.01, 1.005, bz + 0.04, 0, new THREE.Vector3(0.1, 0.025, 0.01)));
    // Its foot wedged in a cairn.
    for (let i = 0; i < 4; i++) kit.add(rock, STONE[(i + 1) % 3] as string, m, place(bx + Math.cos(i * 1.7) * 0.1, 0.05, bz + Math.sin(i * 1.7) * 0.1, i, 0.8));
  }

  // The other roads, shut: a cart on its side across the mouth, its wheels up, logs heaped against it, stones.
  map.sectors.forEach((_, i) => {
    if (i === road) return;
    const mo = mouthOf(ctx, map, i);
    if (!mo) return;
    const { at, yaw } = mo;
    const m = place(at.x, ctx.ground(at.x, at.z), at.z, yaw);
    // The cart, tipped on its long side across the mouth (built upright in its own frame, then rolled over).
    const cm = m.clone().multiply(place(-0.05, 0.28, 0.14, 0.15 + i * 0.1, 1, -1.38, 0));
    PLANK.forEach((c, k) => kit.add(box, c, cm, place(0, 0, -0.17 + k * 0.17, 0, new THREE.Vector3(1.0, 0.05, 0.155))));
    for (const z of [-0.25, 0.25]) kit.add(box, wood(i + 2), cm, place(0, 0.09, z, 0, new THREE.Vector3(1.0, 0.18, 0.04)));
    for (const x of [-0.5, 0.5]) kit.add(box, wood(i + 3), cm, place(x, 0.09, 0, 0, new THREE.Vector3(0.04, 0.18, 0.5)));
    // Under it, the axle and its wheels, now standing out on the road side; the shafts sticking out of the front.
    kit.add(cylAx, IRON, cm, place(0.28, -0.1, 0, 0, new THREE.Vector3(1, 0.7, 1), Math.PI / 2));
    for (const z of [-0.33, 0.33]) {
      kit.add(wheel, wood(i + 1), cm, place(0.28, -0.1, z, 0, 1, Math.PI / 2));
      kit.add(rim, IRON, cm, place(0.28, -0.1, z));
    }
    for (const z of [-0.14, 0.14]) kit.add(log, wood(i), cm, place(0.78, -0.02, z, 0, new THREE.Vector3(0.3, 0.6, 0.3), 0, Math.PI / 2));
    // The other wheel, come off and fallen against it.
    kit.add(wheel, wood(i + 2), m, place(-0.62, 0.13, -0.12, 0.6, 1, 1.15, 0.3));
    // Logs heaped on the clearing side, crossed.
    kit.add(log, wood(i + 2), m, place(0.05, 0.08, -0.18, 0, new THREE.Vector3(1, 1.3, 1), 0, Math.PI / 2 + 0.12));
    kit.add(log, wood(i + 3), m, place(-0.1, 0.2, -0.24, 0.3, new THREE.Vector3(0.9, 1.1, 0.9), 0, Math.PI / 2 - 0.2));
    kit.add(log, wood(i), m, place(0.5, 0.25, 0.32, -0.5, new THREE.Vector3(0.85, 0.9, 0.85), 0.9, 0.2));
    for (let k = 0; k < 6; k++) kit.add(rock, STONE[k % 3] as string, m, place(-0.7 + k * 0.28, 0.04, 0.35 - (k % 2) * 0.55, k, 0.7 + (k % 3) * 0.25));
  });

  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.9, metalness: 0.05, flatShading: true });
  const mesh = kit.build(mat, 'bet-stakes');
  for (const g of [pole, tip, box, rock, wheel, log, rim, cylAx]) g.dispose();
  if (!mesh) {
    mat.dispose();
    return null;
  }
  mesh.castShadow = !ctx.low;
  mesh.receiveShadow = true;
  return mesh;
};
